import { Heading, LevelData } from "../data/balance";
import { Cell, headingOffset, opposite } from "./cell";

/**
 * What a cell is, for the simulation.
 *
 * `Chasm` is the one that is not obvious: it is a gap in the ground, so nothing can be BUILT on it, but the road may
 * cross it — that crossing is what puts a plank bridge there. Before it existed a chasm was just another `Blocked`
 * cell, the road could never be routed through one, and so the bridge models the kit ships were never once placed in
 * a level. Anything that asks "can I stand here" must answer no for a chasm unless the road bridges it.
 */
export enum CellKind { Empty = 0, Blocked = 1, Chasm = 2, Base = 3 }

/**
 * Static level geometry: size, blocked cells, the base and the heading the road leaves it in. Immutable.
 *
 * The base is a fortress three cells wide and two deep (art/DEV_HANDOFF.md §1). `base` is its **gate** — the
 * front-centre cell the road grows from and enemies walk to — and `footprint` is all six cells: the gate, the two
 * beside it, and the row behind (away from the heading). Every footprint cell is `CellKind.Base`, so nothing can be
 * built or routed through the walls; levels place the gate one row in from the edge so the back row is on the map.
 */
export class Grid {
  private readonly kinds: Uint8Array;
  readonly cellCount: number;
  /** The six cells the fortress occupies (only those in bounds), gate first. */
  readonly footprint: readonly Cell[];

  constructor(readonly width: number, readonly height: number, readonly base: Cell, readonly baseHeading: Heading, blocked: readonly Cell[], chasm: readonly Cell[] = []) {
    if (width < 3 || height < 3) throw new Error("Grid must be at least 3x3");
    this.cellCount = width * height;
    this.kinds = new Uint8Array(this.cellCount);
    if (!this.inBounds(base)) throw new Error("Base must be in bounds");
    if (!this.inBounds(base.step(baseHeading))) throw new Error("Base heading must point into the grid");
    this.footprint = Grid.footprintOf(base, baseHeading).filter((c) => this.inBounds(c));
    for (const c of blocked) {
      if (!this.inBounds(c)) throw new Error(`Blocked cell out of bounds: ${c}`);
      if (c.equals(base.step(baseHeading))) throw new Error(`Blocked cell overlaps the base exit: ${c}`);
      if (this.footprint.some((f) => f.equals(c))) continue;      // inside the walls: the fortress already covers it
      this.kinds[this.index(c)] = CellKind.Blocked;
    }
    for (const c of chasm) {
      if (!this.inBounds(c)) throw new Error(`Chasm cell out of bounds: ${c}`);
      if (c.equals(base.step(baseHeading))) throw new Error(`Chasm cell overlaps the base exit: ${c}`);
      if (this.footprint.some((f) => f.equals(c))) continue;      // under the fortress: the walls already cover it
      this.kinds[this.index(c)] = CellKind.Chasm;
    }
    for (const c of this.footprint) this.kinds[this.index(c)] = CellKind.Base;
  }

  /** Gate, the two cells beside it, and the three behind it (may run off the grid; callers filter). */
  static footprintOf(gate: Cell, heading: Heading): Cell[] {
    const [dx, dy] = headingOffset(heading);
    const back = gate.step(opposite(heading));
    const side = (c: Cell, k: number) => new Cell(c.x + dy * k, c.y - dx * k);
    return [gate, side(gate, 1), side(gate, -1), back, side(back, 1), side(back, -1)];
  }

  /** True for any of the fortress's cells, gate included. */
  isBaseCell(c: Cell): boolean { return this.inBounds(c) && this.kinds[this.index(c)] === CellKind.Base; }

  static fromLevel(level: LevelData): Grid {
    return new Grid(level.width, level.height, new Cell(level.baseX, level.baseY), level.baseHeading,
      level.blocked.map((t) => new Cell(t.x, t.y)), level.chasm.map((t) => new Cell(t.x, t.y)));
  }

  inBounds(c: Cell): boolean { return c.x >= 0 && c.y >= 0 && c.x < this.width && c.y < this.height; }
  index(c: Cell): number { return c.y * this.width + c.x; }
  cellAt(index: number): Cell { return new Cell(index % this.width, Math.floor(index / this.width)); }
  kindAt(c: Cell): CellKind { return this.kinds[this.index(c)] as CellKind; }
}
